import { useTranslation } from 'react-i18next';

import { useBetStakeLimit } from '@/feature/betslip-sbk/hooks/use-bet-stake-limit';
import { useIsCoinsCurrency } from '@/feature/betslip-sbk/hooks/use-coins-currency';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { StakeInputErrors } from '@/feature/betslip-sbk/types';
import { MAX_BET_STAKE, MIN_BET_STAKE, stakeInputValidators } from '@/feature/betslip-sbk/utils/validators';
import { MIN_COINS_STAKE, coinsStakeInputValidators } from '@/feature/betslip-sbk/utils/validators-coins';
import { activeLimitsSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';

export const isCoinsError = (error: string): error is keyof typeof coinsStakeInputValidators => {
    return error in coinsStakeInputValidators;
};

export const isSbkError = (error: string): error is keyof typeof stakeInputValidators => {
    return error in stakeInputValidators;
};

/**
 * Returns the error message for the stake input
 */
export const useStakeInputErrorMessage = (betId: string): string | undefined => {
    const { data: limits = [] } = useUserLimits({ select: activeLimitsSelector('SINGLE_WAGER') });
    const { t } = useTranslation(['betslip_sbk', 'sweeps', 'common', 'wallet']);
    const isEditing = useSbkBetSlipStore(state => state.editBetId === betId);
    const { data: stakeLimit } = useBetStakeLimit(betId, isEditing);
    const { formatCurrency } = useFormatCurrency();
    const isCoinsCurrency = useIsCoinsCurrency();
    const isPredictions = useIsPredictionsProduct();
    const error = useSbkBetSlipStore(state => state.bets[betId]?.stakeInputError);
    const isBetrBucks = useSbkBetSlipStore(state => state.isBetrBucksEnabled[betId]);

    if (error) {
        if (isCoinsCurrency && isCoinsError(error)) {
            const key = coinsStakeInputValidators[error].translationKey;
            switch (error) {
                case StakeInputErrors.ExceedSingleWalletBalance:
                    return t(key, { currency: t('sweeps:gold_coins') });
                case StakeInputErrors.BelowMin:
                    return t(key, { value: formatCurrency(MIN_COINS_STAKE) });
                case StakeInputErrors.AboveMax:
                    return t(key, { value: formatCurrency(MAX_BET_STAKE) });
                case StakeInputErrors.DecimalsNotAllowed:
                    return t(key);
                default:
                    return;
            }
        } else if (!isCoinsCurrency && isSbkError(error)) {
            const key = stakeInputValidators[error].translationKey;
            switch (error) {
                case StakeInputErrors.ExceedSingleWalletBalance:
                    if (isPredictions) {
                        return t(key, { currency: t('wallet:cash') });
                    }
                    return t(key, { currency: isBetrBucks ? t('common:bonus') : t('sweeps:currency_cash') });
                case StakeInputErrors.BelowMin:
                    return t(key, { value: formatCurrency(MIN_BET_STAKE) });
                case StakeInputErrors.AboveMax:
                    return t(key, { value: formatCurrency(MAX_BET_STAKE) });
                case StakeInputErrors.ExceedMaxSingleWager:
                    const maxSingleEntryLimit = limits.find(limit => limit.type === 'SINGLE_WAGER');
                    if (maxSingleEntryLimit) {
                        return t(key, { value: formatCurrency(maxSingleEntryLimit.amount) });
                    }
                    break;
                case StakeInputErrors.ExceedStakeLimit:
                    if (stakeLimit !== undefined) {
                        return t(key, { value: formatCurrency(stakeLimit) });
                    }
                    break;
                default:
                    return;
            }
        }
    }
};
